import { useState, useEffect, useCallback, useMemo } from "react";
import { dashboardService } from "@/services/dashboardService";
import { ticketService } from "@/services/ticketService";
import { reportService } from "@/services/reportService";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DateRangePicker } from "@/components/DateRangePicker";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Download, FileText, ShieldCheck, Users, TrendingUp } from "lucide-react";
import { STATUS_OPTIONS, PRIORITY_OPTIONS, CATEGORY_OPTIONS, MODULE_OPTIONS } from "@/utils/constants";
import { toast } from "sonner";

const COLORS = ["#003DA5", "#3B82F6", "#8B5CF6", "#F59E0B", "#10B981", "#52525B", "#EF4444"];
const SLA_COLORS = { met: "#10B981", breached: "#EF4444" };
const PRIORITY_COLORS = { critical: "#EF4444", high: "#F97316", medium: "#F59E0B", low: "#10B981" };
const AXIS_TICK_SM = { fontSize: 10 };
const BAR_RADIUS_SM = [2, 2, 0, 0];
const BAR_RADIUS_RIGHT = [0, 2, 2, 0];
const PIE_INNER = 50;
const PIE_OUTER = 85;

function TableBodyContent({ loading, isEmpty, skeletonRows, skeletonCols, emptyColSpan, emptyMessage, emptyTestId, children }) {
  if (loading) {
    return Array.from({ length: skeletonRows }, (_, i) => (
      <tr key={i} className="border-b border-[#E4E4E7]">
        {Array.from({ length: skeletonCols }, (_, c) => (
          <td key={c} className="px-3 py-2.5"><div className="h-4 bg-zinc-200 rounded-sm animate-pulse" /></td>
        ))}
      </tr>
    ));
  }
  if (isEmpty) {
    return <tr><td colSpan={emptyColSpan} className="px-3 py-8 text-center text-[#52525B]" data-testid={emptyTestId}>{emptyMessage}</td></tr>;
  }
  return children;
}

function toISODate(d) { return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString() : ""; }
function toISODateEnd(d) { return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).toISOString() : ""; }

export default function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [slaData, setSlaData] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(undefined);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- service imports and setState are stable references
  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (dateRange?.from) params.date_from = toISODate(dateRange.from);
    if (dateRange?.to) params.date_to = toISODateEnd(dateRange.to);
    Promise.all([
      dashboardService.getStats(params).then(({ data }) => setStats(data)).catch(() => {}),
      reportService.getSlaCompliance(params).then(({ data }) => setSlaData(data)).catch(() => {}),
      reportService.getAgentPerformance(params).then(({ data }) => setAgentData(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="p-6"><div className="animate-pulse space-y-6"><div className="h-8 w-48 bg-zinc-200 rounded-sm" /><div className="h-64 bg-zinc-200 rounded-sm" /></div></div>;

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold tracking-tight text-[#09090B]">Reports & Analytics</h1><p className="text-sm text-[#52525B] mt-0.5">Insights, SLA compliance, agent performance, and data exports</p></div>
        <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="rounded-sm bg-[#F4F4F5] h-9">
          <TabsTrigger value="overview" className="rounded-sm text-sm gap-1.5 data-[state=active]:bg-white" data-testid="tab-overview"><TrendingUp size={14} /> Overview</TabsTrigger>
          <TabsTrigger value="sla" className="rounded-sm text-sm gap-1.5 data-[state=active]:bg-white" data-testid="tab-sla"><ShieldCheck size={14} /> SLA Compliance</TabsTrigger>
          <TabsTrigger value="agents" className="rounded-sm text-sm gap-1.5 data-[state=active]:bg-white" data-testid="tab-agents"><Users size={14} /> Agent Performance</TabsTrigger>
          <TabsTrigger value="export" className="rounded-sm text-sm gap-1.5 data-[state=active]:bg-white" data-testid="tab-export"><Download size={14} /> Export</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          {stats && <OverviewTab stats={stats} />}
        </TabsContent>

        <TabsContent value="sla" className="mt-4 space-y-4">
          {slaData ? <SLAComplianceTab slaData={slaData} /> : <p className="text-sm text-[#52525B] text-center py-8">No SLA data available</p>}
        </TabsContent>

        <TabsContent value="agents" className="mt-4 space-y-4">
          {agentData && agentData.length > 0 ? <AgentPerformanceTab agentData={agentData} /> : <p className="text-sm text-[#52525B] text-center py-8">No agent performance data available</p>}
        </TabsContent>

        <TabsContent value="export" className="mt-4 space-y-4">
          <ExportTab dateRange={dateRange} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OverviewTab({ stats }) {
  const statusChartData = useMemo(() => Object.entries(stats.status_breakdown || {}).map(([k, v]) => ({ name: k.replace("_", " "), value: v })), [stats.status_breakdown]);
  const priorityChartData = useMemo(() => Object.entries(stats.priority_breakdown || {}).map(([k, v]) => ({ name: k, value: v })), [stats.priority_breakdown]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Total Tickets" value={stats.total_tickets} />
        <MetricCard label="Open" value={stats.open_tickets} />
        <MetricCard label="In Progress" value={stats.in_progress_tickets} />
        <MetricCard label="Resolved" value={stats.resolved_tickets} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Tickets by Status" testId="report-status-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={statusChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" /><XAxis dataKey="name" tick={AXIS_TICK_SM} /><YAxis tick={AXIS_TICK_SM} allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#003DA5" radius={BAR_RADIUS_SM} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Tickets by Priority" testId="report-priority-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={priorityChartData} cx="50%" cy="50%" innerRadius={PIE_INNER} outerRadius={PIE_OUTER} dataKey="value" label>
                {priorityChartData.map((entry) => <Cell key={entry.name} fill={PRIORITY_COLORS[entry.name] || COLORS[0]} />)}
              </Pie><Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>
  );
}

function SLAComplianceTab({ slaData }) {
  const responsePieData = useMemo(() => [{ name: "Met", value: slaData.response_met }, { name: "Breached", value: slaData.response_breached }], [slaData.response_met, slaData.response_breached]);
  const resolutionPieData = useMemo(() => [{ name: "Met", value: slaData.resolution_met }, { name: "Breached", value: slaData.resolution_breached }], [slaData.resolution_met, slaData.resolution_breached]);

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Tracked Tickets" value={slaData.total_tracked} />
        <MetricCard label="Resp. Compliance" value={`${slaData.response_compliance}%`} accent={complianceAccent(slaData.response_compliance)} />
        <MetricCard label="Resol. Compliance" value={`${slaData.resolution_compliance}%`} accent={complianceAccent(slaData.resolution_compliance)} />
        <MetricCard label="Avg Response" value={`${slaData.avg_response_hours}h`} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Response SLA" testId="sla-response-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart><Pie data={responsePieData} cx="50%" cy="50%" innerRadius={PIE_INNER} outerRadius={PIE_OUTER} dataKey="value" label><Cell fill={SLA_COLORS.met} /><Cell fill={SLA_COLORS.breached} /></Pie><Tooltip /><Legend /></PieChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Resolution SLA" testId="sla-resolution-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart><Pie data={resolutionPieData} cx="50%" cy="50%" innerRadius={PIE_INNER} outerRadius={PIE_OUTER} dataKey="value" label><Cell fill={SLA_COLORS.met} /><Cell fill={SLA_COLORS.breached} /></Pie><Tooltip /><Legend /></PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <Card className="border-[#E4E4E7] shadow-none rounded-sm" data-testid="sla-by-priority">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-bold tracking-tight">Compliance by Priority</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E4E4E7] bg-[#F4F4F5]">
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Priority</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Total</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Resp. Met</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Resp. %</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Resol. Met</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Resol. %</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(slaData.by_priority).map(([priority, data]) => (
                  <tr key={priority} className="border-b border-[#E4E4E7]" data-testid={`sla-priority-row-${priority}`}>
                    <td className="px-3 py-2 capitalize font-medium">{priority}</td>
                    <td className="px-3 py-2">{data.total}</td>
                    <td className="px-3 py-2">{data.response_met}</td>
                    <td className="px-3 py-2"><ComplianceBadge value={data.response_compliance} /></td>
                    <td className="px-3 py-2">{data.resolution_met}</td>
                    <td className="px-3 py-2"><ComplianceBadge value={data.resolution_compliance} /></td>
                  </tr>
                ))}
                {Object.keys(slaData.by_priority).length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-[#52525B]">No SLA data yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function AgentPerformanceTab({ agentData }) {
  return (
    <>
      <ChartCard title="Tickets per Agent" testId="agent-tickets-chart" height="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={agentData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
            <XAxis type="number" tick={AXIS_TICK_SM} allowDecimals={false} />
            <YAxis type="category" dataKey="name" tick={AXIS_TICK_SM} width={100} />
            <Tooltip />
            <Bar dataKey="resolved_tickets" name="Resolved" fill="#10B981" stackId="a" />
            <Bar dataKey="open_tickets" name="Open" fill="#3B82F6" stackId="a" radius={BAR_RADIUS_RIGHT} />
            <Legend />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <Card className="border-[#E4E4E7] shadow-none rounded-sm" data-testid="agent-performance-table">
        <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-bold tracking-tight">Agent Details — Individual Contribution</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E4E4E7] bg-[#F4F4F5]">
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Agent</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Role</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Assigned</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Resolved</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Tickets Touched</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Holding Time</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Comments</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Time Logged</th>
                  <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">SLA %</th>
                </tr>
              </thead>
              <tbody>
                {agentData.map((agent) => (
                  <tr key={agent.agent_id} className="border-b border-[#E4E4E7]" data-testid={`agent-row-${agent.agent_id}`}>
                    <td className="px-3 py-2 font-medium">{agent.name}</td>
                    <td className="px-3 py-2 capitalize text-[#52525B]">{agent.role}</td>
                    <td className="px-3 py-2">{agent.total_assigned}</td>
                    <td className="px-3 py-2">{agent.resolved_tickets}</td>
                    <td className="px-3 py-2">{agent.tickets_touched || 0}</td>
                    <td className="px-3 py-2">{agent.total_holding_hours > 0 ? `${agent.total_holding_hours}h` : "—"}</td>
                    <td className="px-3 py-2">{agent.comments_made || 0}</td>
                    <td className="px-3 py-2">{agent.time_logged_hours > 0 ? `${agent.time_logged_hours}h` : "—"}</td>
                    <td className="px-3 py-2"><ComplianceBadge value={agent.sla_compliance} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function ExportTab({ dateRange }) {
  const [exportFilters, setExportFilters] = useState({ status: "", priority: "", category: "", module: "" });
  const [previewData, setPreviewData] = useState([]);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(true);

  useEffect(() => {
    setPreviewLoading(true);
    const params = { limit: 100 };
    if (exportFilters.status) params.status = exportFilters.status;
    if (exportFilters.priority) params.priority = exportFilters.priority;
    if (exportFilters.category) params.category = exportFilters.category;
    if (exportFilters.module) params.module = exportFilters.module;
    if (dateRange?.from) params.date_from = toISODate(dateRange.from);
    if (dateRange?.to) params.date_to = toISODateEnd(dateRange.to);
    ticketService.list(params).then(({ data }) => { setPreviewData(data.tickets); setPreviewTotal(data.total); }).catch(() => {}).finally(() => setPreviewLoading(false));
  }, [exportFilters, dateRange]);

  const buildExportParams = () => {
    const params = {};
    if (exportFilters.status) params.status = exportFilters.status;
    if (exportFilters.priority) params.priority = exportFilters.priority;
    if (exportFilters.category) params.category = exportFilters.category;
    if (exportFilters.module) params.module = exportFilters.module;
    if (dateRange?.from) params.date_from = toISODate(dateRange.from);
    if (dateRange?.to) params.date_to = toISODateEnd(dateRange.to);
    return params;
  };

  return (
    <div className="space-y-4">
      <ExportControls exportFilters={exportFilters} setExportFilters={setExportFilters} buildExportParams={buildExportParams} />
      <ExportPreviewTable loading={previewLoading} data={previewData} total={previewTotal} />
    </div>
  );
}

function ExportControls({ exportFilters, setExportFilters, buildExportParams }) {
  const handleExportCSV = async () => {
    try {
      const response = await ticketService.exportCsv(buildExportParams());
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", "tickets_export.csv");
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  const handleExportPDF = async () => {
    try {
      const response = await reportService.exportPdf(buildExportParams());
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", "tickets_report.pdf");
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success("PDF exported");
    } catch { toast.error("PDF export failed"); }
  };

  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-bold tracking-tight flex items-center gap-2"><FileText size={15} /> Export Tickets</CardTitle></CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <p className="text-xs text-[#52525B] uppercase tracking-[0.1em] font-bold">Status</p>
            <Select value={exportFilters.status || "all"} onValueChange={(v) => setExportFilters({ ...exportFilters, status: v === "all" ? "" : v })}>
              <SelectTrigger className="w-[130px] h-8 rounded-sm text-sm" data-testid="export-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-[#52525B] uppercase tracking-[0.1em] font-bold">Priority</p>
            <Select value={exportFilters.priority || "all"} onValueChange={(v) => setExportFilters({ ...exportFilters, priority: v === "all" ? "" : v })}>
              <SelectTrigger className="w-[120px] h-8 rounded-sm text-sm" data-testid="export-priority-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-[#52525B] uppercase tracking-[0.1em] font-bold">Category</p>
            <Select value={exportFilters.category || "all"} onValueChange={(v) => setExportFilters({ ...exportFilters, category: v === "all" ? "" : v })}>
              <SelectTrigger className="w-[130px] h-8 rounded-sm text-sm" data-testid="export-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-[#52525B] uppercase tracking-[0.1em] font-bold">Module</p>
            <Select value={exportFilters.module || "all"} onValueChange={(v) => setExportFilters({ ...exportFilters, module: v === "all" ? "" : v })}>
              <SelectTrigger className="w-[120px] h-8 rounded-sm text-sm" data-testid="export-module-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All</SelectItem>{MODULE_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={handleExportCSV} data-testid="export-csv-button" className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white gap-1.5 h-8"><Download size={14} /> Export CSV</Button>
          <Button onClick={handleExportPDF} variant="outline" data-testid="export-pdf-button" className="rounded-sm gap-1.5 h-8"><FileText size={14} /> Export PDF</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ExportPreviewTable({ loading, data, total }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-sm font-bold tracking-tight">Preview ({total} ticket{total !== 1 ? "s" : ""} match)</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="export-preview-table">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#F4F4F5]">
                {["Ticket", "Title", "Status", "Priority", "Category", "Module", "Assignee", "Created"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableBodyContent loading={loading} isEmpty={data.length === 0} skeletonRows={3} skeletonCols={8} emptyColSpan={8} emptyMessage="No tickets match the selected filters" emptyTestId="no-export-data">
                {data.map((t) => <ExportPreviewRow key={t.id} ticket={t} />)}
              </TableBodyContent>
            </tbody>
          </table>
        </div>
        {total > 100 && (
          <div className="px-3 py-2 border-t border-[#E4E4E7] bg-[#F4F4F5]">
            <p className="text-xs text-[#52525B]">Showing first 100 of {total} tickets. Export will include all matching tickets.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ExportPreviewRow({ ticket: t }) {
  return (
    <tr className="border-b border-[#E4E4E7] hover:bg-zinc-50 transition-colors" data-testid={`export-row-${t.ticket_number}`}>
      <td className="px-3 py-2 font-mono text-xs text-[#52525B]">{t.ticket_number}</td>
      <td className="px-3 py-2 text-[#09090B] font-medium max-w-[250px] truncate">{t.title}</td>
      <td className="px-3 py-2 capitalize text-xs">{t.status?.replace("_", " ")}</td>
      <td className="px-3 py-2 capitalize text-xs">{t.priority}</td>
      <td className="px-3 py-2 capitalize text-xs">{t.category?.replace(/_/g, " ")}</td>
      <td className="px-3 py-2 text-xs">{MODULE_OPTIONS.find((m) => m.value === t.module)?.label || t.module || "—"}</td>
      <td className="px-3 py-2 text-xs text-[#52525B]">{t.assigned_to_name || "-"}</td>
      <td className="px-3 py-2 text-xs text-[#52525B]">{t.created_at?.slice(0, 10)}</td>
    </tr>
  );
}

function complianceAccent(value) {
  if (value >= 80) return "green";
  if (value >= 50) return "amber";
  return "red";
}

function MetricCard({ label, value, accent }) {
  const ACCENT_CLASSES = { green: "text-emerald-600", red: "text-red-600", amber: "text-amber-600" };
  const accentClass = ACCENT_CLASSES[accent] || "text-[#09090B]";
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm" data-testid={`metric-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <CardContent className="p-4 text-center">
        <p className={`text-2xl font-bold font-heading ${accentClass}`}>{value}</p>
        <p className="text-xs text-[#52525B] uppercase tracking-[0.1em] font-bold mt-1">{label}</p>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, children, testId, height = "h-[240px]" }) {
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm">
      <CardHeader className="pb-2"><CardTitle className="font-heading text-sm font-bold tracking-tight">{title}</CardTitle></CardHeader>
      <CardContent><div className={height} data-testid={testId}>{children}</div></CardContent>
    </Card>
  );
}

function ComplianceBadge({ value }) {
  let cls = "bg-red-50 text-red-700 border-red-200";
  if (value >= 80) cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (value >= 50) cls = "bg-amber-50 text-amber-700 border-amber-200";
  return <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium border rounded-sm ${cls}`}>{value}%</span>;
}
